<template>
  <v-card fluid flat tile min-height="92vh" class="back mb-n15 pa-6 px-md-15">
    <!-- Modern Header -->
    <div v-if="showHeader" class="header-section mb-6" style="position: relative;">
      <!-- Close Button -->
      <v-btn icon small class="header-close-btn" @click="dismissHeader">
        <v-icon color="white" size="18">mdi-close</v-icon>
      </v-btn>
      <div class="d-flex align-center mb-4">
        <div class="header-icon-wrapper mr-4">
          <v-icon size="32" color="#FFFFFF">mdi-flash-auto</v-icon>
        </div>
        <div>
          <h1 class="page-title mb-1">Automation</h1>
          <p class="page-subtitle mb-0">
            Automate your workflow with intelligent rules and triggers
          </p>
        </div>
        <v-spacer></v-spacer>
        <div class="d-none d-sm-flex">
          <v-chip
            class="mr-2 white--text"
            small
            style="background: rgba(255,255,255,0.25) !important; border: 1px solid rgba(255,255,255,0.5);"
          >
            <v-icon left x-small color="white">mdi-brain</v-icon>
            Smart Rules
          </v-chip>
          <v-chip
            class="mr-2 white--text"
            small
            style="background: rgba(255,255,255,0.25) !important; border: 1px solid rgba(255,255,255,0.5);"
          >
            <v-icon left x-small color="white">mdi-lightning-bolt</v-icon>
            Auto Actions
          </v-chip>
          <v-chip
            class="white--text"
            small
            style="background: rgba(255,255,255,0.25) !important; border: 1px solid rgba(255,255,255,0.5);"
          >
            <v-icon left x-small color="white">mdi-clock-fast</v-icon>
            Time Saving
          </v-chip>
        </div>
      </div>

      <!-- Feature Pills -->
      <v-row class="feature-pills ma-0">
        <v-col cols="6" sm="4" md="2" class="pa-1">
          <div class="feature-pill">
            <v-icon size="18" color="white" class="mr-2">mdi-swap-horizontal</v-icon>
            <span>Disposition Change</span>
          </div>
        </v-col>
        <v-col cols="6" sm="4" md="2" class="pa-1">
          <div class="feature-pill">
            <v-icon size="18" color="white" class="mr-2">mdi-format-list-bulleted</v-icon>
            <span>List Management</span>
          </div>
        </v-col>
        <v-col cols="6" sm="4" md="2" class="pa-1">
          <div class="feature-pill">
            <v-icon size="18" color="white" class="mr-2">mdi-webhook</v-icon>
            <span>Webhooks</span>
          </div>
        </v-col>
        <v-col cols="6" sm="4" md="2" class="pa-1">
          <div class="feature-pill">
            <v-icon size="18" color="white" class="mr-2">mdi-message-text-outline</v-icon>
            <span>Auto Messaging</span>
          </div>
        </v-col>
        <v-col cols="6" sm="4" md="2" class="pa-1">
          <div class="feature-pill">
            <v-icon size="18" color="white" class="mr-2">mdi-bell-outline</v-icon>
            <span>Notifications</span>
          </div>
        </v-col>
      </v-row>
    </div>

    <!-- Create New Automation CTA -->
    <div class="create-cta-card mb-8">
      <div class="create-cta-content text-center">
        <div class="create-cta-icon-wrapper mb-4">
          <v-icon size="40" color="#4caf50">mdi-flash-auto</v-icon>
        </div>
        <h3 class="create-cta-title mb-2">Create New Automation</h3>
        <p class="create-cta-subtitle mb-5">
          Automate your workflow by setting up rules that trigger actions<br>
          based on disposition changes, list updates, and more.
        </p>
        <v-btn
          large
          depressed
          color="#4caf50"
          class="create-cta-btn white--text"
          @click="selectListDialog = true"
        >
          <v-icon left>mdi-plus-circle-outline</v-icon>
          Create Automation
        </v-btn>
      </div>
    </div>

    <!-- List Automation Card -->
    <!-- List Automation Card -->
    <v-card
      v-for="list in lists"
      v-show="!loading"
      :key="list.id"
      class="list-automation-card mb-8"
      elevation="2"
    >
      <!-- Green Gradient Header -->
      <div class="list-card-header">
        <div class="d-flex align-center">
          <div class="list-header-icon mr-3">
            <v-icon size="26" color="#FFFFFF">mdi-format-list-bulleted</v-icon>
          </div>
          <div>
            <h3 class="list-header-title">{{ list.name }}</h3>
            <p class="list-header-subtitle mb-0">Configure automation rules for this list</p>
          </div>
        </div>
        <v-btn
          class="add-rule-header-btn"
          elevation="0"
          @click="openAddRule(list.id)"
        >
          <div class="d-flex align-center">
            <v-icon size="20" color="primary" class="mr-2">mdi-plus-circle-outline</v-icon>
            <div>
              <div class="add-rule-header-title">ADD NEW RULE</div>
              <div class="add-rule-header-sub">CREATE AUTOMATION WORKFLOW</div>
            </div>
          </div>
        </v-btn>
      </div>

      <!-- Card Content: Rules -->
      <v-card-text class="list-card-content pa-5">
        <automation-rules
          :ref="'rules_' + list.id"
          :list="list"
          :all-lists="allLists"
          @refresh="refresh()"
        />
      </v-card-text>
    </v-card>

    <!-- Loading State -->
    <div v-if="loading" class="text-center pa-12">
      <v-skeleton-loader
        type="card-heading, list-item-three-line, card-heading, list-item-three-line"
        class="mx-auto"
        style="max-width: 900px;"
      ></v-skeleton-loader>
    </div>

    <!-- Empty State -->
    <div v-if="!loading && lists.length === 0" class="text-center pa-12">
      <v-icon size="80" color="grey lighten-2" class="mb-4">mdi-robot-outline</v-icon>
      <h3 class="grey--text text--darken-1 mb-2">No Automation Rules</h3>
      <p class="grey--text">Use the button above to create your first automation</p>
    </div>

    <!-- Select List Dialog -->
    <v-dialog v-model="selectListDialog" width="560" persistent>
      <v-card class="modern-dialog-card">
        <div class="select-dialog-header">
          <div class="d-flex align-center">
            <div class="select-dialog-icon-badge mr-3">
              <v-icon color="#FFFFFF">mdi-format-list-bulleted</v-icon>
            </div>
            <div>
              <h3 class="select-dialog-title mb-0">Create Automation</h3>
              <p class="select-dialog-subtitle mb-0">Select a list to add an automation rule</p>
            </div>
          </div>
          <v-btn icon color="white" @click="selectListDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-divider></v-divider>
        <v-card-text class="pa-6">
          <label class="field-label">Choose a List</label>
          <v-autocomplete
            v-model="selectedCreateList"
            :items="allLists"
            item-text="name"
            item-value="id"
            label="Select List"
            outlined
            dense
            hide-details="auto"
            auto-select-first
          >
            <template v-slot:no-data>
              <div class="px-4 py-2">No lists available</div>
            </template>
          </v-autocomplete>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-6">
          <v-spacer></v-spacer>
          <v-btn large outlined color="grey darken-1" class="mr-3" @click="selectListDialog = false">
            <v-icon left>mdi-close</v-icon>
            Cancel
          </v-btn>
          <v-btn large depressed color="primary" :disabled="!selectedCreateList" @click="openRuleDialogForList">
            <v-icon left>mdi-arrow-right</v-icon>
            Next
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { mapGetters } from "vuex";
import automationRules from "../components/automationRules.vue";
const _ = require("lodash");
export default {
  components: { automationRules },
  data: () => ({
    lists: [],
    allLists: [],
    rules: [],
    loading: false,
    selectListDialog: false,
    selectedCreateList: "",
    showHeader: true,
  }),
  computed: {
    ...mapGetters([
      "ENDPOINT",
      "dispoList",
      "teams",
      "selectedTeam",
      "selectedList",
    ]),
  },
  async created() {
    if (localStorage.getItem("automationHeaderDismissed") === "true") {
      this.showHeader = false;
    }
    await this.fetchLists();
  },
  methods: {
    dismissHeader() {
      this.showHeader = false;
      localStorage.setItem("automationHeaderDismissed", "true");
    },
    openAddRule(listId) {
      this.$nextTick(() => {
        const refKey = "rules_" + listId;
        const refArray = this.$refs[refKey];
        if (refArray && refArray.length > 0) {
          refArray[0].openDialog();
        }
      });
    },
    openRuleDialogForList() {
      const listId = this.selectedCreateList;
      if (!listId) return;
      this.selectListDialog = false;

      // If the list isn't currently displayed, add it temporarily
      const alreadyDisplayed = this.lists.some((l) => l.id === listId);
      if (!alreadyDisplayed) {
        const listObj = this.allLists.find((l) => l.id === listId);
        if (listObj) {
          this.lists.push(listObj);
        }
      }

      this.$nextTick(() => {
        const refKey = "rules_" + listId;
        const refArray = this.$refs[refKey];
        if (refArray && refArray.length > 0) {
          refArray[0].openDialog();
        }
      });
      this.selectedCreateList = "";
    },
    async refresh() {
      console.log("Refresh in automation Parent");
      await this.fetchLists();
    },
    getTeamIds() {
      var teams = [];
      if (this.selectedTeam == "allTeams") {
        teams = _.map(this.teams, "id");
      } else {
        teams.push(this.selectedTeam);
      }
      teams = _.without(teams, "allTeams");
      return teams;
    },
    async fetchLists() {
      this.lists = [];
      this.allLists = [];
      this.loading = true;
      var teamIds = await this.getTeamIds();
      try {
        var res = await this.$http.post(`${this.ENDPOINT}/lists/fetch`, { teams: teamIds });
        var fetchedLists = res.body || [];
        this.allLists = fetchedLists;
        console.log("All lists", fetchedLists);

        // Filter to only show lists that have automations
        var listsWithAutomation = [];
        for (var i = 0; i < fetchedLists.length; i++) {
          var list = fetchedLists[i];
          try {
            var autoRes = await this.$http.get(
              `${this.ENDPOINT}/automations?filter[where][listId]=${list.id}`
            );
            if (Array.isArray(autoRes.body) && autoRes.body.length > 0) {
              listsWithAutomation.push(list);
              continue;
            }
          } catch (e) {
            console.log("Error checking automations for list", list.id, e);
          }
          // Fallback: check old-style triggers
          try {
            var trigRes = await this.$http.get(
              `${this.ENDPOINT}/lists/${list.id}?filter[fields][triggers]=true`
            );
            if (trigRes.body && Array.isArray(trigRes.body.triggers) && trigRes.body.triggers.length > 0) {
              listsWithAutomation.push(list);
            }
          } catch (e) {
            console.log("Error checking triggers for list", list.id, e);
          }
        }
        this.lists = listsWithAutomation;
      } catch (e) {
        console.log(e);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* Page Header */
.header-section {
  background: linear-gradient(135deg, #37cf37 0%, #2ba82b 100%);
  border-radius: 16px;
  padding: 32px;
  color: white;
  box-shadow: 0 10px 40px rgba(55, 207, 55, 0.3);
}

.header-close-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(255, 255, 255, 0.2) !important;
  z-index: 1;
}

.header-icon-wrapper {
  background: rgba(255, 255, 255, 0.2);
  width: 56px;
  height: 56px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  margin: 0;
  color: white;
}

.page-subtitle {
  font-size: 15px;
  opacity: 0.95;
  color: rgba(255, 255, 255, 0.9);
}

/* Feature Pills */
.feature-pills {
  margin-top: 20px;
}

.feature-pill {
  background: rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  font-size: 13px;
  color: white;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
}

.feature-pill:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: translateY(-2px);
}

/* List Card */
.list-automation-card {
  border-radius: 12px !important;
  overflow: hidden;
}

/* Green Gradient Header */
.list-card-header {
  background: linear-gradient(to right, #6aad6a, #88c388, #a3d4a3);
  padding: 20px 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.list-header-icon {
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.25);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.list-header-title {
  color: #ffffff;
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 2px;
  line-height: 1.3;
}

.list-header-subtitle {
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  font-weight: 400;
  line-height: 1.3;
}

/* Add New Rule Button in Header */
.add-rule-header-btn {
  background: #ffffff !important;
  border-radius: 14px !important;
  padding: 12px 24px !important;
  height: auto !important;
  min-height: 56px !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  box-shadow: none !important;
}

.add-rule-header-btn:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12) !important;
}

.add-rule-header-title {
  font-size: 14px;
  font-weight: 700;
  color: #37cf37;
  line-height: 1.3;
  text-transform: uppercase;
  text-align: left;
}

.add-rule-header-sub {
  font-size: 11px;
  font-weight: 600;
  color: #9e9e9e;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  text-align: left;
}

/* Card Content */
.list-card-content {
  background: #f5f7f5;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
}

/* Create New Automation CTA */
.create-cta-card {
  border: 2px solid #4caf50;
  border-radius: 12px;
  background: #ffffff;
  padding: 48px 24px;
}

.create-cta-content {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.create-cta-icon-wrapper {
  width: 80px;
  height: 80px;
  background: rgba(76, 175, 80, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.create-cta-title {
  font-size: 22px;
  font-weight: 700;
  color: #212121;
}

.create-cta-subtitle {
  font-size: 15px;
  color: #757575;
  line-height: 1.6;
}

.create-cta-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  letter-spacing: 0 !important;
  padding: 12px 32px !important;
  height: auto !important;
}

/* Select List Dialog */
.modern-dialog-card {
  border-radius: 12px !important;
  overflow: hidden !important;
}

.select-dialog-header {
  background: linear-gradient(135deg, #4caf50 0%, #388e3c 100%);
  padding: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.select-dialog-icon-badge {
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.select-dialog-title {
  color: #ffffff;
  font-size: 20px;
  font-weight: 600;
}

.select-dialog-subtitle {
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
}

.field-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #495057;
  margin-bottom: 8px;
}
</style>
